const millisecondsInADay = 86_400_000;

export const daysSince = (sinceDate?: string): number => {
  if (!sinceDate) {
    return 1;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const waitingSince = new Date(sinceDate);
  waitingSince.setHours(0, 0, 0, 0);
  return Math.round(
    (today.getTime() - waitingSince.getTime()) / millisecondsInADay,
  );
};

export const toIsoString = (date?: string | Date): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed = date instanceof Date ? date : new Date(date);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

/**
 * Converts a date to an ISO date string.
 * @param date The Date object to convert.
 * @returns A date string in the following format: yyyy-mm-dd.
 */
export const toIsoDateString = (date?: string | Date): string | undefined => {
  if (!date) {
    return;
  }

  const isoString = toIsoString(date);
  if (!isoString) {
    return;
  }

  return isoString.split('T')[0];
};

/**
 * Converts a date to a 2-digit month and year.
 * @param date The Date object to convert.
 * @returns A date string in the following format: mm/yy.
 */
export const toTwoDigitDateString = (date?: Date): string | undefined => {
  if (!date) {
    return;
  }

  const options: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    year: '2-digit',
  };

  return new Intl.DateTimeFormat('en-US', options).format(date);
};

/** Generate select items for the 12 calendar months. */
export const generateMonthItems = () =>
  Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    return {
      value: month.toString(),
      // TODO: locale-aware month names
      text: Intl.DateTimeFormat('en', { month: 'long' }).format(
        new Date(2000, i),
      ),
    };
  });

/** Generate select items for days 1–31. */
export const generateDayItems = () =>
  Array.from({ length: 31 }, (_, i) => {
    const day = i + 1;
    return {
      value: day.toString(),
      text: day.toString(),
    };
  });

/**
 * Formats a date string or Date object into a short human-readable format.
 * @param date - ISO date string or Date object
 * @returns Formatted string (e.g. "Jun 2, 2026") or undefined if invalid
 */
export const toShortDateString = (
  date?: string | Date | null,
): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formats a date string or Date object into a long human-readable format with time.
 * @param date - ISO date string or Date object
 * @returns Formatted string (e.g. "June 2, 2026, 5:34 PM") or undefined if invalid
 */
export const toLongDate12hrTimeString = (
  date?: string | Date | null,
): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(parsed);
};

/**
 * Formats a date to short time only (e.g. "8:00 AM").
 * Returns undefined for empty or invalid input.
 */
export const toShortTimeString = (
  date?: string | Date | null,
): string | undefined => {
  if (!date) {
    return undefined;
  }
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

/**
 * Checks whether two date/time ranges overlap.
 * Two ranges overlap when one starts before the other ends and vice versa.
 */
export const doDateRangesOverlap = (
  startA: string | Date,
  endA: string | Date,
  startB: string | Date,
  endB: string | Date,
): boolean => {
  const a0 = new Date(startA).getTime();
  const a1 = new Date(endA).getTime();
  const b0 = new Date(startB).getTime();
  const b1 = new Date(endB).getTime();
  return a0 < b1 && a1 > b0;
};

/**
 * Formats a date using UTC timezone to prevent hydration mismatches.
 * Thin wrapper over toLocaleDateString with timeZone: 'UTC' baked in.
 */
export const formatDateUTC = (
  date: Date,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-US',
): string => date.toLocaleDateString(locale, { ...options, timeZone: 'UTC' });

/**
 * Formats a date to short time using UTC timezone.
 * E.g. "7:00 PM"
 */
export const formatTimeUTC = (date: Date, locale = 'en-US'): string =>
  date.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });

/**
 * Formats a date range for display using UTC timezone.
 * Handles same-day, same-year, and cross-year ranges.
 *
 * @returns e.g. "March 12, 2026", "March 12 – March 14, 2026", "December 2025 – January 2026"
 */
export const formatDateRangeUTC = (
  start: string | Date,
  end: string | Date,
  options?: {
    /** Use month-only format (no day). Useful for tour/multi-month ranges. */
    monthOnly?: boolean;
  },
): string => {
  const startDate = start instanceof Date ? start : new Date(start);
  const endDate = end instanceof Date ? end : new Date(end);
  const monthOnly = options?.monthOnly ?? false;

  const isSameDay = formatDateUTC(startDate, {}) === formatDateUTC(endDate, {});
  const isSameYear = startDate.getUTCFullYear() === endDate.getUTCFullYear();

  if (monthOnly) {
    const opts: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' };
    const startStr = formatDateUTC(startDate, opts);
    const endStr = formatDateUTC(endDate, opts);
    return startStr === endStr ? startStr : `${startStr} – ${endStr}`;
  }

  const endOpts: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  };

  if (isSameDay) {
    return formatDateUTC(startDate, endOpts);
  }

  const startOpts: Intl.DateTimeFormatOptions = isSameYear
    ? { month: 'long', day: 'numeric' }
    : { month: 'long', day: 'numeric', year: 'numeric' };

  return `${formatDateUTC(startDate, startOpts)} – ${formatDateUTC(endDate, endOpts)}`;
};

/**
 * Formats a date or date range for event-detail display using UTC timezone.
 *
 * - Single day: "Thu • March 12, 2026 • 7:00 PM"
 * - Multi-day (same year): "Thu, March 12 – Sat, March 14, 2026"
 * - Multi-day (cross year): "Thu, December 30, 2025 – Sat, January 1, 2026"
 */
export const formatEventDateUTC = (
  start: string | Date,
  end: string | Date,
): string => {
  const startDate = start instanceof Date ? start : new Date(start);
  const endDate = end instanceof Date ? end : new Date(end);

  const isSameDay = formatDateUTC(startDate, {}) === formatDateUTC(endDate, {});

  if (isSameDay) {
    const weekday = formatDateUTC(startDate, { weekday: 'short' });
    const fullDate = formatDateUTC(startDate, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    return `${weekday} \u2022 ${fullDate} \u2022 ${formatTimeUTC(startDate)}`;
  }

  const isSameYear = startDate.getUTCFullYear() === endDate.getUTCFullYear();
  const startOpts: Intl.DateTimeFormatOptions = isSameYear
    ? { weekday: 'short', month: 'long', day: 'numeric' }
    : { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' };
  const endOpts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  };

  return `${formatDateUTC(startDate, startOpts)} – ${formatDateUTC(endDate, endOpts)}`;
};

/**
 * Formats a date-key string (YYYY-MM-DD) for slot display using UTC timezone.
 *
 * - 'long': "Saturday, September 26" (for date group headers)
 * - 'short': "Sat, Sep 26" (for inline confirmation display)
 */
export const formatSlotDateUTC = (
  dateKey: string,
  style: 'long' | 'short' = 'long',
): string => {
  const d = new Date(`${dateKey.slice(0, 10)}T00:00:00Z`);
  const options: Intl.DateTimeFormatOptions =
    style === 'short'
      ? { weekday: 'short', month: 'short', day: 'numeric' }
      : { weekday: 'long', month: 'long', day: 'numeric' };
  return d.toLocaleDateString('en-US', { ...options, timeZone: 'UTC' });
};
